import { useState } from 'react';
import { useAuth } from 'context/AuthContext';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '@util/firebase';
import { MasterDocument } from '@util/firestore/master';
import NotifyMeModal from './NotifyMeModal';

interface NotifyMeModuleProps {
  master: MasterDocument;
  /**
   * Fired once the watch is actually registered — after the authenticated
   * one-click POST succeeds, or after the guest modal's POST succeeds.
   * Optional and additive: every existing call site (the /m/ page empty
   * state) omits it and behaves exactly as before. Added for GB #1401's
   * search zero-result state, which needs a `zero_state_notify_submit`
   * GA4 event on successful submit without touching /api/master/watch.
   */
  onSuccess?: () => void;
}

export default function NotifyMeModule({ master, onSuccess }: NotifyMeModuleProps) {
  const { user, userDoc } = useAuth();
  const [isWatching, setIsWatching] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [guestSubmitted, setGuestSubmitted] = useState(false);
  // GB #1401 final gate round fix (H1): the authenticated one-click path's
  // catch rolled isWatching back to false but showed the user nothing — the
  // button just flickered back to "Notify Me ->" with no error, no toast, no
  // retry hint, and (correctly) no zero_state_notify_submit event, so the
  // failure was invisible in both the UI and analytics. Match the guest
  // modal's existing errorMessage pattern (NotifyMeModal.tsx) instead of
  // adding a new one.
  const [authErrorMessage, setAuthErrorMessage] = useState('');

  const alreadyWatching =
    isWatching || (userDoc?.watchedModels?.includes(master.id) ?? false);

  const cardClass =
    'bg-[#fbfbfb] rounded-[2rem] shadow-[2px_2px_15px_0px_rgba(0,0,0,0.25)] p-[2.4rem] flex flex-col items-center text-center gap-[1.2rem]';

  // --- State 1: Authenticated + already watching ---
  if (user && userDoc && alreadyWatching) {
    return (
      <div className={`${cardClass} justify-center`}>
        <p className="text-[1.8rem] font-extrabold text-black leading-[1.3]">
          <span className="text-green-600">&#10003;</span> You&apos;re on the list.
        </p>
        <p className="text-[1.4rem] text-brand-dark-gray leading-[1.5]">
          {/* GB #1401 final gate round fix (M2): see NotifyMeModal.tsx's
              matching comment — {master.brand} {master.model} isn't
              guaranteed present on the Algolia-sourced object the search
              zero-state passes in; `title` is what the render gate already
              requires and what the rest of that card already shows. */}
          We&apos;ll notify you the moment a {master.title} is listed.
        </p>
      </div>
    );
  }

  // --- State 2: Authenticated + NOT watching ---
  if (user && userDoc) {
    const handleWatch = async () => {
      setIsWatching(true);
      setAuthErrorMessage('');

      try {
        await updateDoc(doc(db, 'users', userDoc.uid), {
          watchedModels: arrayUnion(master.id),
        });

        const res = await fetch('/api/master/watch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            masterId: master.id,
            masterTitle: master.title,
            email: userDoc.email,
            uid: userDoc.uid,
          }),
        });

        // GB #1401 gate-review fix (HIGH): fetch() does not reject on
        // HTTP 4xx/5xx, so a rate-limited (429) or otherwise-rejected
        // request previously still showed "You're on the list" and fired
        // zero_state_notify_submit for a write that never happened. Match
        // NotifyMeModal's guest path, which already checks res.ok.
        if (!res.ok) {
          throw new Error(`watch request failed with status ${res.status}`);
        }

        onSuccess?.();
      } catch {
        // Roll back the optimistic isWatching(true) above so the UI doesn't
        // lock into a false "watching" state when the server rejected the
        // request.
        setIsWatching(false);
        // GB #1401 final gate round fix (H1): this used to be the only
        // outcome — no message, no toast, no retry hint. A shopper on a
        // shared IP that just hit the rate limit (or any other failure)
        // saw the button silently revert with nothing telling them why, and
        // correctly no zero_state_notify_submit fired, so the failure was
        // invisible in analytics too. Same generic copy as the guest
        // modal's errorMessage (NotifyMeModal.tsx) — the res.status isn't
        // shown verbatim, it isn't meaningful to a shopper.
        setAuthErrorMessage('Something went wrong. Please try again.');

        // GB #1401 gate-review fix (MEDIUM): the arrayUnion write above ran
        // BEFORE the POST, so a rejected (429/500) request left a stale
        // watchedModels entry in Firestore even though the rollback above
        // resets local state. AuthContext keeps userDoc live via a snapshot
        // listener, and alreadyWatching also reads userDoc.watchedModels —
        // so without this, the stale entry makes the UI show "you're on the
        // list" as true again (from the live snapshot), permanently and
        // cross-device, for a watch that was never actually registered
        // server-side. Roll back the write too, in its own try/catch so a
        // failure here doesn't throw an unhandled error.
        try {
          await updateDoc(doc(db, 'users', userDoc.uid), {
            watchedModels: arrayRemove(master.id),
          });
        } catch {
          // Swallow — matches this file's existing error-handling style
          // (the isWatching rollback above also has no logging/toast). A
          // failure here just leaves a stale watchedModels entry, which is
          // the SAME symptom this fix targets, not a worse one; it does not
          // need to fail loudly to the user.
        }
      }
    };

    return (
      <div className={cardClass}>
        <p className="text-[1.8rem] font-extrabold text-black leading-[1.3]">
          Notify me when one lists.
        </p>
        <p className="text-[1.4rem] text-brand-dark-gray leading-[1.5]">
          We&apos;ll send you an alert the moment a {master.title} is
          available. One click &mdash; we&apos;ll use your account email.
        </p>
        {authErrorMessage && (
          <p className="text-[1.3rem] text-red-600">{authErrorMessage}</p>
        )}
        <button
          onClick={handleWatch}
          className="mt-auto inline-block w-full bg-black text-white font-bold rounded-[1rem] px-[2.4rem] py-[0.8rem] text-[1.4rem] text-center hover:bg-gray-800 transition-colors duration-150 cursor-pointer"
        >
          Notify Me &rarr;
        </button>
      </div>
    );
  }

  // --- State 3: Guest (success) ---
  if (guestSubmitted) {
    return (
      <div className={`${cardClass} justify-center`}>
        <p className="text-[1.8rem] font-extrabold text-black leading-[1.3]">
          <span className="text-green-600">&#10003;</span> You&apos;re on the list.
        </p>
        <p className="text-[1.4rem] text-brand-dark-gray leading-[1.5]">
          We&apos;ll notify you as soon as a {master.title} is listed on
          GearFocus.
        </p>
      </div>
    );
  }

  // --- State 3: Guest (idle) ---
  return (
    <>
      <div className={cardClass}>
        <p className="text-[1.8rem] font-extrabold text-black leading-[1.3]">
          Get notified when one lists.
        </p>
        <p className="text-[1.4rem] text-brand-dark-gray leading-[1.5]">
          We&apos;ll text or email you the moment a {master.title} is listed
          on GearFocus.
        </p>
        <button
          onClick={() => setShowModal(true)}
          className="mt-auto inline-block w-full bg-black text-white font-bold rounded-[1rem] px-[2.4rem] py-[0.8rem] text-[1.4rem] text-center hover:bg-gray-800 transition-colors duration-150 cursor-pointer"
        >
          Get Notified &rarr;
        </button>
      </div>

      <NotifyMeModal
        master={master}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={() => {
          setGuestSubmitted(true);
          onSuccess?.();
        }}
      />
    </>
  );
}
